import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { transactionService } from '@services/transactionService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'transactions';

/**
 * Hook para gerenciar transações
 */
export const useTransactions = (filters = {}) => {
  const queryClient = useQueryClient();

  // Listar transações com paginação
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: ({ pageParam }) => transactionService.getAll({ ...filters, lastKey: pageParam }),
    getNextPageParam: (lastPage) => lastPage.lastKey || undefined,
    initialPageParam: undefined,
  });

  // Flatten pages
  const transactions = data?.pages?.flatMap(page => page.transactions) || [];

  // Criar transação
  const createMutation = useMutation({
    mutationFn: transactionService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Transação criada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar transação');
    },
  });

  // Atualizar transação
  const updateMutation = useMutation({
    mutationFn: ({ transactionId, date, data }) => 
      transactionService.update(transactionId, date, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Transação atualizada com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao atualizar transação');
    },
  });

  // Deletar transação
  const deleteMutation = useMutation({
    mutationFn: ({ transactionId, date }) => transactionService.delete(transactionId, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Transação excluída com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao excluir transação');
    },
  });

  return {
    transactions,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    createTransaction: createMutation.mutateAsync,
    updateTransaction: updateMutation.mutateAsync,
    deleteTransaction: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

export default useTransactions;
